import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { Observable, Subject } from 'rxjs';
import { first, take, takeUntil } from 'rxjs/operators';
import _ from 'lodash';

import { Account } from '../../models/account';
import { BlogArticle } from '../../models/content';
import { AccountService } from '../../services/account.service';
import { ModalService } from '../../services/modal.service';

import { PicalunaSiteService } from '../../services/picaluna-site.service';
import { ContentBlogComponent } from './content-blog.component';

/**
 * Content module — Blog articles list. Clone of the region/charity list:
 * persists via accountService.patchContent('blogs', ...) (the RBAC-gated
 * /account/content route), HARD delete (the route replaces the whole
 * collection; use the `published` toggle to keep a draft/unpublish instead).
 */
@Component({
  selector: 'app-content-blog-list',
  templateUrl: './content-blog-list.component.html',
})
export class ContentBlogListComponent implements OnInit, OnDestroy {
  /** True until ensureFullAccount() resolves. account$ replays a cached,
   *  content-STRIPPED account instantly, so gating on the observable alone
   *  renders an empty table instead of the loading state. */
  accountLoading = true;

  private unsubscribe$ = new Subject<void>();
  account$: Observable<Account>;

  /** Slug currently being published, so only that row shows a spinner. */
  publishingSlug: string | null = null;

  constructor(
    private modalService: ModalService,
    private accountService: AccountService,
    private dialogService: DialogService,
    private picalunaSiteService: PicalunaSiteService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.account$ = this.accountService.account$;
  }

  ngOnInit(): void {
    // The default account fetch omits the content subtree (payload diet);
    // load it on demand so this editor has data to show.
    this.accountService.ensureFullAccount().subscribe({
      next: () => (this.accountLoading = false),
      error: () => (this.accountLoading = false),
    });
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  blogsAsList(content: any): BlogArticle[] {
    const data = content?.blogs;
    if (!data) return [];
    const list = Array.isArray(data) ? data.filter(Boolean) : Object.values(data).filter(Boolean);
    return _.sortBy(list as BlogArticle[], (b) => b?._meta?.index ?? Number.MAX_SAFE_INTEGER);
  }

  /**
   * Publish just this article to the website.
   *
   * Scoped publishing (picaluna-v3 rebuild-pipeline-PLAN.md Part 1): this ships
   * ONLY this post. Every other piece of content stays exactly as currently
   * deployed, so an article somebody else has published in Conductor but is not
   * ready to ship cannot ride along — which is what a plain "Publish preview"
   * would do.
   *
   * Works in both directions. A published article goes live; an article whose
   * Published switch is OFF is REMOVED from the site by the same action, since
   * the scope is "make the site match Conductor for this one item". The confirm
   * copy says which one is about to happen so it is never a surprise.
   */
  onPublishBlog(blog: BlogArticle): void {
    const slug = (blog?.slug || '').trim();
    if (!slug || this.publishingSlug) {
      return;
    }

    const isLive = blog.published === true;
    const title = blog.title || slug;

    const modal = this.modalService.generic({
      title: isLive ? 'Publish this article?' : 'Remove this article from the site?',
      copy: isLive
        ? [
            `This publishes "${title}" to the website preview.`,
            'Only this article is published — everything else on the site stays exactly as it is now.',
          ]
        : [
            `"${title}" is currently a draft, so publishing it will REMOVE it from the website.`,
            'Turn the Published switch on first if you meant to put it live. Nothing else on the site changes either way.',
          ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-text' },
        {
          label: isLive ? 'Publish this article' : 'Remove from site',
          key: 'publish',
          class: isLive ? 'p-button-success' : 'p-button-danger',
        },
      ],
    });

    modal?.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (key: string) => {
        if (key !== 'publish') {
          return;
        }

        this.publishingSlug = slug;

        this.picalunaSiteService
          .publish(this.publishScope('blogs', blog))
          // take(1), not takeUntil: the publish DISPATCH must outlive the
          // dialog — takeUntil aborted it mid-flight and skipped the
          // publishedSlug breadcrumb, so the NEXT rename lost its redirect.
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.publishingSlug = null;
              // Remember what shipped, so a later rename can take this page
              // down instead of leaving the old URL live.
              this.recordPublishedSlug(blog._meta.id, slug);
              this.modalService.generic({
                title: 'Publishing started',
                copy: [
                  `"${title}" is being published. It takes a few minutes.`,
                  'Track progress on the Website page, then publish to production from there when you are happy with the preview.',
                ],
                buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
              });
            },
            error: (err) => {
              this.publishingSlug = null;
              this.modalService.error(err?.error?.error || err);
            },
          });
      },
    });
  }

  onAddBlog(): void {
    const account = this.snapshot();
    if (!account) return;
    const existing = this.blogsAsList(account.data?.content);
    const seed: BlogArticle = {
      _meta: { id: `id-blog-${this.shortId()}`, index: this.nextIndex(existing) },
      slug: '',
      title: '',
      published: false, // new posts start as drafts
    };
    this.openEditor(seed, true);
  }

  onEditBlog(blog: BlogArticle): void {
    this.openEditor(_.cloneDeep(blog), false);
  }

  onDeleteBlog(blog: BlogArticle): void {
    const modal = this.modalService.generic({
      title: 'Remove blog article',
      copy: [
        `Remove "${blog.title || blog._meta.id}"? Its page will stay on picaluna.com until the NEXT publish — run one when ready. To hide it without deleting, uncheck "Published" instead.`,
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: 'Remove', key: 'confirm', class: 'p-button-danger', icon: 'pi pi-trash' },
      ],
    });
    modal.onClose.subscribe({
      next: (reason: string) => {
        if (reason?.toString().toLowerCase() !== 'confirm') return;
        const account = this.snapshot();
        if (!account?.data?.content?.blogs || !Array.isArray(account.data.content.blogs)) return;
        account.data.content.blogs = (account.data.content.blogs as BlogArticle[]).filter(
          (b) => b?._meta?.id !== blog._meta.id
        );
        this.persistRemove(blog._meta.id);
      },
    });
  }

  private openEditor(blog: BlogArticle, isNew: boolean): void {
    const ref = this.dialogService.open(ContentBlogComponent, {
      // Esc would close the editor and silently discard unsaved changes.
      closeOnEscape: false,
      header: isNew ? 'New blog article' : `Edit ${blog.title || 'article'}`,
      width: '90vw',
      baseZIndex: 11000,
      data: {
        blog,
        isNew,
        // Editor-performed save: it closes only once the server confirms
        // (Save spins meanwhile), so a publish cannot race the write.
        save: (b: any) => this.accountService.patchContentItem('blogs', b).pipe(take(1)),
      },
    });
    ref.onClose.subscribe({
      next: (result: { saved: boolean; blog: BlogArticle; persisted?: boolean } | undefined) => {
        if (!result?.saved) return;
        const account = this.snapshot();
        if (!account) return;
        if (!account.data.content) account.data.content = {};
        if (!Array.isArray(account.data.content.blogs)) account.data.content.blogs = [];
        const arr = account.data.content.blogs as BlogArticle[];
        const i = arr.findIndex((b) => b?._meta?.id === result.blog._meta.id);
        if (i >= 0) arr[i] = result.blog;
        else arr.push(result.blog);
        if (!result.persisted) this.persistItem(result.blog);
      },
    });
  }

  /**
   * Writes are take(1) with NO takeUntil.
   *
   * takeUntil(unsubscribe$) on a write ABORTS the in-flight request when the
   * modal closes, so a save or delete could be silently cancelled by closing
   * the dialog that started it — and it completed the stream empty, so first()
   * threw EmptyError ("no elements in sequence"). A content write must outlive
   * its dialog, like recordPublishedSlug below. Errors are surfaced rather than
   * swallowed: a rejected save that leaves the row on screen reads as success.
   */
  // Per-item persistence: sends ONLY the edited/removed record, so two
  // content managers working in different blogs can't clobber each other.
  private persistItem(item: any): void {
    this.accountService
      .patchContentItem('blogs', item)
      .pipe(take(1))
      .subscribe({ error: (err) => this.modalService.error(err?.error?.error || err) });
  }

  private persistRemove(id: string): void {
    this.accountService
      .removeContentItem('blogs', id)
      .pipe(take(1))
      .subscribe({ error: (err) => this.modalService.error(err?.error?.error || err) });
  }

  private snapshot(): Account | undefined {
    let s: Account | undefined;
    this.accountService.account$.pipe(first()).subscribe({ next: (a) => (s = a) });
    return s;
  }
  private nextIndex(existing: BlogArticle[]): number {
    return existing.reduce((acc, b) => {
      const i = b?._meta?.index;
      return typeof i === 'number' && i > acc ? i : acc;
    }, -10) + 10;
  }
  private shortId(): string {
    return Math.random().toString(36).slice(2, 10);
  }

  /**
   * The scope for a per-item publish, taking the OLD page down when the slug
   * changed since it was last published.
   *
   * The scoped merge keys on slug, so a renamed item's previous record is not in
   * scope and gets carried forward untouched while the new one is appended —
   * leaving both URLs live. Sending the old token too resolves it to a takedown,
   * because an id in scope that Conductor no longer has is removed.
   */
  private publishScope(type: string, item: { slug?: string; publishedSlug?: string }): string {
    const slug = (item.slug || '').trim();
    const prev = (item.publishedSlug || '').trim();
    return prev && prev !== slug ? `${type}:${prev} ${type}:${slug}` : `${type}:${slug}`;
  }


  /**
   * Record the slug this item just shipped under.
   *
   * Re-reads the account FIRST. patchContentItem sends the whole record and the
   * server's upsert is delete-then-rewrite with no version check, so writing the
   * copy this dialog has been holding would revert anything another content
   * manager changed since it loaded. Fetching immediately before the write
   * shrinks that window to the request itself.
   *
   * Best-effort throughout: this is a breadcrumb for a later rename, and it must
   * never surface an error over a publish that already succeeded.
   */
  private recordPublishedSlug(metaId: string, slug: string): void {
    // NOT gated on takeUntil(unsubscribe$). This is a fire-and-forget
    // breadcrumb that must outlive the dialog: closing the list during the
    // fetch cancelled the whole chain, so the slug was never recorded and the
    // next rename could not take the old page down. first() completes both
    // observables on their own, so nothing leaks.
    this.accountService
      .get(true)
      .pipe(first())
      .subscribe({
        next: () => {
          const account = this.snapshot();
          const rows = account?.data?.content?.blogs;
          if (!Array.isArray(rows)) return;
          const fresh = (rows as BlogArticle[]).find((r) => r?._meta?.id === metaId);
          if (!fresh || fresh.publishedSlug === slug) return;
          fresh.publishedSlug = slug;
          this.accountService
            .patchContentItem('blogs', fresh)
            .pipe(first())
            .subscribe({ error: () => {} });
        },
        error: () => {},
      });
  }

}
